/**
 * ─── App Colour & Style Tokens ──────────────────────────────────────────────
 *
 * Single source of truth for every colour, spacing and size used across the
 * app.  Import this file wherever you need a colour in inline styles.
 * CSS classes in App.css reference the same values via CSS custom properties
 * (--var-name) that are declared in :root using these constants.
 *
 * Colour families
 *   brand       Primary blue (nav, edit, filters)
 *   success     Green  (add, active, completed)
 *   danger      Red    (delete, error, not-filled)
 *   warning     Orange (sending, incomplete)
 *   neutral     Greys  (muted text, borders, disabled)
 *   surface     Backgrounds (page, card, input)
 *   purple      Apartment navigation button
 *
 * Badge colours
 *   badgeGreen  Guestbook COMPLETED / Ubyport SENT
 *   badgeOrange Guestbook INCOMPLETE / Ubyport SENDING
 *   badgeRed    Guestbook NOT_FILLED / Ubyport FAILED
 *   badgeGray   Guestbook DEPRECATED / Ubyport NOT_SENT
 *
 * Sizes
 *   actionBtnSize  Square icon-only button side length (px)
 *   actionBtnH     Height of labelled action buttons (px)
 */

export const colors = {
  // ── Brand (blue) — matches landing page #2563eb (Tailwind blue-600) ────
  brand:            '#2563eb',
  brandHover:       '#1d4ed8',
  brandDark:        '#1e3a8a',   // active nav, nav selected state, deep navy
  brandLight:       '#dbeafe',   // active nav background
  brandFaint:       '#eff6ff',   // nav hover background

  // ── Success (green) ─────────────────────────────────────────────────────
  success:          '#16a34a',
  successHover:     '#15803d',

  // ── Danger (red) ────────────────────────────────────────────────────────
  danger:           '#dc2626',
  dangerHover:      '#b91c1c',

  // ── Warning (orange / amber) ─────────────────────────────────────────────
  warning:          '#d97706',
  warningHover:     '#b45309',

  // ── Purple (apartment / secondary actions) ───────────────────────────────
  purple:           '#4f46e5',
  purpleHover:      '#4338ca',

  // ── Neutral palette (Tailwind slate) ────────────────────────────────────
  textPrimary:      '#0f172a',
  textSecondary:    '#1e293b',
  textMuted:        '#475569',
  textSubtle:       '#64748b',
  textDisabled:     '#94a3b8',

  borderSoft:       '#e2e8f0',
  borderInput:      '#cbd5e1',

  bgPage:           '#f8fafc',
  bgSurface:        '#ffffff',
  bgInput:          '#f8fafc',

  disabled:         '#94a3b8',   // toggle-off / inactive compact
  disabledBg:       '#e2e8f0',

  // ── Badge colours ────────────────────────────────────────────────────────
  // Green  – COMPLETED / SENT
  badgeGreenBg:     '#c6f6d5',
  badgeGreenText:   '#22543d',

  // Orange – INCOMPLETE / SENDING
  badgeOrangeBg:    '#feebc8',
  badgeOrangeText:  '#7b341e',

  // Red    – NOT_FILLED / SEND_FAILED
  badgeRedBg:       '#fed7d7',
  badgeRedText:     '#742a2a',

  // Gray   – DEPRECATED / NOT_SENT
  badgeGrayBg:      '#e2e8f0',
  badgeGrayText:    '#2d3748',
} as const;

/** Standard height (px) for every labelled action button in list rows */
export const ACTION_BTN_H = 32;

/** Standard side length (px) for square icon-only buttons */
export const ICON_BTN_SIZE = 32;

/** Standard border radius for action buttons */
export const ACTION_BTN_RADIUS = 6;

